import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiLink, FiTrash2, FiClock, FiAlertTriangle } from "react-icons/fi";

import api from "@/services/api";
import { useComponentTranslation } from "@/services/i18n";
import Loader from "@/components/loader";

const translations = {
  en: {
    title: "Connected applications",
    description: "Third-party apps that can access your Jobego account.",
    empty_title: "No connected applications",
    empty_desc: "Apps you authorize (e.g. Claude) will appear here.",
    connected_on: "Connected",
    last_used: "Last used",
    never_used: "Never used",
    revoke: "Revoke",
    revoke_confirm: "Revoke access for this application?",
    revoked: "Access revoked",
    revoke_failed: "Revocation failed",
    propagation_notice: "Revocation takes effect immediately for new sessions. A currently active session may remain usable for up to 1 hour.",
  },
  fr: {
    title: "Applications connectées",
    description: "Applications tierces autorisées à accéder à votre compte Jobego.",
    empty_title: "Aucune application connectée",
    empty_desc: "Les applications que vous autorisez (ex. Claude) apparaîtront ici.",
    connected_on: "Connectée le",
    last_used: "Dernière utilisation",
    never_used: "Jamais utilisée",
    revoke: "Révoquer",
    revoke_confirm: "Révoquer l'accès à cette application ?",
    revoked: "Accès révoqué",
    revoke_failed: "Échec de la révocation",
    propagation_notice: "La révocation est effective immédiatement pour les nouvelles sessions. Une session active peut rester utilisable jusqu'à 1 heure.",
  },
};

function formatDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleString();
}

export default function ConnectedApps() {
  const { t } = useComponentTranslation(translations);
  const [grants, setGrants] = useState(null);
  const [revokingId, setRevokingId] = useState(null);

  async function load() {
    try {
      const res = await api.get("/oauth/grants");
      setGrants(res.grants || []);
    } catch (e) {
      toast.error(e?.error_description || "Failed to load");
      setGrants([]);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function revoke(grant) {
    if (!window.confirm(t("revoke_confirm"))) return;
    try {
      setRevokingId(grant._id);
      await api.remove(`/oauth/grants/${grant._id}`);
      toast.success(t("revoked"));
      setGrants((prev) => prev.filter((g) => g._id !== grant._id));
    } catch (e) {
      toast.error(e?.error_description || t("revoke_failed"));
    } finally {
      setRevokingId(null);
    }
  }

  return (
    <div className="max-w-2xl mx-auto py-8">
      <div className="text-center mb-10">
        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight mb-3">{t("title")}</h1>
        <p className="text-gray-500">{t("description")}</p>
      </div>

      {grants === null && (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      )}

      {grants !== null && grants.length === 0 && (
        <div className="bg-white border-2 border-black rounded-2xl p-10 text-center shadow-[4px_4px_0_0_rgba(0,0,0,1)]">
          <div className="w-14 h-14 rounded-2xl border-2 border-black flex items-center justify-center mx-auto mb-5 bg-gray-50">
            <FiLink className="w-6 h-6 text-gray-400" />
          </div>
          <h3 className="text-lg font-extrabold text-gray-900 mb-2">{t("empty_title")}</h3>
          <p className="text-sm text-gray-500">{t("empty_desc")}</p>
        </div>
      )}

      {grants && grants.length > 0 && (
        <>
          <div className="space-y-3">
            {grants.map((g) => (
              <div
                key={g._id}
                className="bg-white border-2 border-black rounded-2xl p-5 flex items-start justify-between gap-4 shadow-[4px_4px_0_0_rgba(0,0,0,1)]">
                <div className="flex items-start gap-4 min-w-0">
                  <div className="w-11 h-11 rounded-xl border-2 border-black bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <FiLink className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-extrabold text-gray-900 truncate">{g.client_name || g.client_id}</p>
                    <div className="mt-1 flex flex-col gap-0.5 text-xs text-gray-500">
                      <span>{t("connected_on")} {formatDate(g.created_at)}</span>
                      <span className="flex items-center gap-1">
                        <FiClock className="w-3 h-3" />
                        {g.last_used_at ? `${t("last_used")} ${formatDate(g.last_used_at)}` : t("never_used")}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  className="btn btn-secondary btn-small shrink-0"
                  onClick={() => revoke(g)}
                  disabled={revokingId === g._id}
                >
                  <FiTrash2 className="w-4 h-4" />
                  {t("revoke")}
                </button>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-start gap-2 text-xs text-gray-500 justify-center text-center max-w-md mx-auto">
            <FiAlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <p>{t("propagation_notice")}</p>
          </div>
        </>
      )}
    </div>
  );
}
